import React, { useState } from "react";
import Sidebar from "./sidebar";
import axios from "../../utils/axios";
import toast, { Toaster } from "react-hot-toast";
import { AnimatePresence, motion } from "framer-motion";
import { trackAction } from "../../utils/clickTracking";

const AddProperty = () => {
  const User = JSON.parse(localStorage.getItem("user")) || {};
  const addedBy = User?.id || "Unknown";
  const circle = User?.circle || "Unknown";

  const getEffectiveRank = (u) => {
    const username = String(u?.username || "").trim().toLowerCase();
    const flagType = String(u?.flag_verification_type || "").trim().toLowerCase();

    if (username === "inspector9") return 9;
    if (username === "inspector6") return 6;
    if (username === "inspector1") return 4;
    if (username === "inspector2") return 8;

    if (flagType === "review_cv") return 6;
    if (flagType === "review_supervisor") return 9;
    if (flagType === "add_cv") return 4;
    if (flagType === "add_supervisor") return 8;

    if (flagType === "ai") return 6;
    if (flagType === "supervisor") return 9;

    return Number(u.rank);
  };

  const userRank = getEffectiveRank(User);
  const isRank8 = userRank === 8;
  const isRank1 = userRank === 4;
  const isS1DUser = isRank8 || isRank1;
  const shouldSkipRefNo = isRank8 || isRank1;

  const initialFeatures = [
    { name: "Floor", before: "", after: "" },
    { name: "Land Use", before: "", after: "" },
    { name: "Land Area", before: "", after: "" },
    { name: "Covered Area", before: "", after: "" },
  ];

  const [pin, setPin] = useState("");
  const [coment, setComent] = useState("");
  const [features, setFeatures] = useState(initialFeatures);
  const [otherLandUse, setOtherLandUse] = useState(""); // For "Other" option
  const [showOtherInput, setShowOtherInput] = useState(false); // To show/hide other input

  // Modal & PT-5
  const [showRefModal, setShowRefModal] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const pinLength = pin.length;
  const isValidS1DPin = /^\d{22}$/.test(pin);
  const hasInvalidS1DPinLength = isS1DUser && pinLength > 0 && !isValidS1DPin;

  const landUseOptions = [
    "Residential",
    "Commercial",
    "Mixed",
    "Other"
  ];

  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...features];
    
    // Special handling for Land Use (index 1)
    if (index === 1 && field === 'before') {
      if (value === "Other") {
        setShowOtherInput(true);
        updatedFeatures[index][field] = ""; // Temporarily empty until user specifies
      } else {
        setShowOtherInput(false);
        setOtherLandUse("");
        updatedFeatures[index][field] = value;
      }
    } else if (index === 1 && field === 'after') {
      if (value === "Other") {
        setShowOtherInput(true);
        updatedFeatures[index][field] = ""; // Temporarily empty until user specifies
      } else {
        setShowOtherInput(false);
        setOtherLandUse("");
        updatedFeatures[index][field] = value;
      }
    } else {
      updatedFeatures[index][field] = value;
    }
    
    setFeatures(updatedFeatures);
  };

  const handleOtherLandUseChange = (field, value) => {
    setOtherLandUse(value);
    const updatedFeatures = [...features];
    updatedFeatures[1][field] = value; // Update the land use field with the "Other" value
    setFeatures(updatedFeatures);
  };

  // Open modal for PT-5 or submit directly for ranks that skip ref
  const handleNext = () => {
    if (!pin.trim() || !circle) {
      toast.error("Please fill the PIN first!");
      return;
    }
    if (shouldSkipRefNo) {
      handleDirectSubmit();
    } else {
      setShowRefModal(true);
    }
  };

  // Submit function for ranks that skip PT-5 (only PIN / basic data)
  const handleDirectSubmit = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("pin", pin);
      formData.append("circle", circle);
      formData.append("group", "AI");
      formData.append("userId", addedBy);
      formData.append("floor_admin", features[0].before);
      formData.append("landuse_admin", features[1].before);
      formData.append("landarea_admin", features[2].before);
      formData.append("coveredarea_admin", features[3].before);

      formData.append("floors_pred", features[0].after);
      formData.append("land_use_pred", features[1].after);
      formData.append("coveredarea_combined_pred", features[3].after);
      formData.append("landarea_combined_pred", features[2].after);

      if (referenceNumber) {
        formData.append("refno", referenceNumber);
      }
      
      await axios.post("/properties", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Property added successfully!");

      // Track add property event
      await trackAction("add_property", pin);
      
      // Reset fields
      setPin("");
      setComent("");
      setFeatures(initialFeatures);
      setReferenceNumber("");
      setShowRefModal(false);
      setOtherLandUse("");
      setShowOtherInput(false);
    } catch (error) {
      console.error("Error adding property:", error);
      if (error.response?.data?.error) {
        if (error.response.data.duplicatePin) {
          toast.error(`PT-5 Serial Number already exists for PIN: ${error.response.data.duplicatePin}`);
        } else {
          toast.error(error.response.data.error);
        }
      } else {
        toast.error("Failed to add property.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Final submit with PT-5 reference (for other ranks)
  const handleFinalSubmit = async () => {
    if (!referenceNumber) return;

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("pin", pin);
      formData.append("circle", circle);
      formData.append("group", "AI")
      formData.append("floor_admin", features[0].before);
      formData.append("landuse_admin", features[1].before);
      formData.append("landarea_admin", features[2].before);
      formData.append("coveredarea_admin", features[3].before);

      formData.append("floors_pred", features[0].after);
      formData.append("land_use_pred", features[1].after);
      formData.append("coveredarea_combined_pred", features[3].after);
      formData.append("landarea_combined_pred", features[2].after);

      formData.append("userId", addedBy);
      formData.append("refno", referenceNumber);

      await axios.post("/properties", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Property added successfully!");

      // Track add property event
      await trackAction("add_property", pin);

      // Reset fields
      setPin("");
      setComent("");
      setFeatures(initialFeatures);
      setReferenceNumber("");
      setShowRefModal(false);
      setOtherLandUse("");
      setShowOtherInput(false);
    } catch (error) {
      console.error("Error adding property:", error);
      if (error.response?.data?.error) {
        if (error.response.data.duplicatePin) {
          toast.error(`PT-5 Serial Number already exists for PIN: ${error.response.data.duplicatePin}`);
        } else {
          toast.error(error.response.data.error);
        }
      } else {
        toast.error("Failed to add property.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8">
        <Toaster position="top-right" />

        <h1 className="text-3xl font-bold text-gray-800 mb-8 border-b pb-4">
          Inspector Dashboard{" "}
          <span className="text-blue-600">– Add Property</span>
        </h1>

        <div className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-gray-600 font-medium mb-2">
                  PIN Number
                </label>
                <input
                  type="text"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter PIN"
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-describedby={
                    hasInvalidS1DPinLength ? "s1d-pin-length-warning" : undefined
                  }
                />
                {hasInvalidS1DPinLength && (
                  <p
                    id="s1d-pin-length-warning"
                    className="mt-2 text-sm text-amber-700"
                    role="status"
                  >
                    Warning: S1D PINs should contain exactly 22 digits. This PIN has{" "}
                    {pinLength}.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Features Table (only show for non-rank8) */}
          {false && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Features Comparison (Fill Anyone to continue)
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Feature
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Before
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        After
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {features.map((feature, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-700">
                          {feature.name}
                        </td>
                        <td className="px-4 py-2">
                          {feature.name === "Land Use" ? (
                            <div className="space-y-2">
                              <select
                                value={showOtherInput ? "Other" : feature.before}
                                onChange={(e) =>
                                  handleFeatureChange(index, "before", e.target.value)
                                }
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select Land Use</option>
                                {landUseOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              {showOtherInput && (
                                <input
                                  type="text"
                                  value={otherLandUse}
                                  onChange={(e) => handleOtherLandUseChange("before", e.target.value)}
                                  placeholder="Please specify land use"
                                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              )}
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={feature.before}
                              onChange={(e) =>
                                handleFeatureChange(index, "before", e.target.value)
                              }
                              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {feature.name === "Land Use" ? (
                            <div className="space-y-2">
                              <select
                                value={showOtherInput ? "Other" : feature.after}
                                onChange={(e) =>
                                  handleFeatureChange(index, "after", e.target.value)
                                }
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select Land Use</option>
                                {landUseOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              {showOtherInput && (
                                <input
                                  type="text"
                                  value={otherLandUse}
                                  onChange={(e) => handleOtherLandUseChange("after", e.target.value)}
                                  placeholder="Please specify land use"
                                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              )}
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={feature.after}
                              onChange={(e) =>
                                handleFeatureChange(index, "after", e.target.value)
                              }
                              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Comment (only show for non-rank8) */}
          {false && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Comment</h2>
              <input
                type="text"
                value={coment}
                onChange={(e) => setComent(e.target.value)}
                placeholder="Enter Comment"
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              onClick={handleNext}
              disabled={submitting || !pin.trim()}
              className={`px-8 py-3 text-white font-semibold rounded-lg shadow transition ${
                submitting || !pin.trim()
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              data-click-id="add_property_save"
            >
              {submitting ? "Saving..." : shouldSkipRefNo ? "Save" : "NEXT"}
            </button>
          </div>
        </div>
      </div>

      {/* PT-5 Serial Number Modal (only show for non-rank8) */}
      <AnimatePresence>
        {!isRank8 && showRefModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
            >
              <h2 className="text-xl font-bold mb-4 text-gray-800">
                Enter PT-5 Serial Number
              </h2>

              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="PT-5 Serial Number"
                className="w-full p-3 border rounded-md mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {!referenceNumber && (
                <p className="text-red-500 text-sm mb-3">
                  Reference number is required
                </p>
              )}

              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleFinalSubmit}
                  disabled={!referenceNumber || submitting}
                  className={`flex-1 px-4 py-2 rounded text-white 
                    ${referenceNumber
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"}`}
                  data-click-id="pt5_save"
                >
                  {submitting ? "Saving..." : "Save"}
                </button>

                <button
                  onClick={() => setShowRefModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                  data-click-id="pt5_cancel"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AddProperty;
